/**
 * FormTemplateDetailPage — fiche modèle (F2-Forms-1c-i / 9.U.4).
 *
 * Actif : métadonnées éditables (nom/description/tags — code & langue en lecture
 * seule), Save explicite + unsaved guard, liste des versions, archivage.
 * Archivé : bannière + lecture seule intégrale, versions historiques lisibles,
 * aucune mutation. Le CTA « Modifier le brouillon » n'est rendu que si la route
 * d'édition existe dans ce déploiement (EDITOR_ROUTE_ENABLED — 1c-ii) : jamais de
 * lien mort (décision PO §10).
 */
import { Schemas } from "@kollectivia/shared";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { TagsInput } from "../../components/TagsInput";
import { useAuth } from "../../contexts/AuthContext";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import {
  type FormTemplateRow,
  type FormTemplateVersionRow,
  useCreateFormVersion,
  useFormTemplate,
  useFormTemplateVersions,
  useRestoreFormTemplate,
  useUpdateFormTemplate,
} from "../../lib/queries/formTemplates";
import { toast } from "../../lib/toast";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";

import { ArchiveFormTemplateDialog } from "./_parts/ArchiveFormTemplateDialog";
import { FormTemplateStatusBadge, FormVersionStateBadge } from "./_parts/FormTemplateBadges";
import { PublishFormVersionDialog } from "./_parts/PublishFormVersionDialog";
import { formFillPath, formVersionEditorPath } from "./editorRoute";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatDate(iso: string, lang: "fr" | "en"): string {
  try {
    return new Intl.DateTimeFormat(`${lang}-CA`, { dateStyle: "medium" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function sameTags(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function FormTemplateDetailPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { id } = useParams<{ id: string }>();

  const detail = useFormTemplate(id);
  const versions = useFormTemplateVersions(id);
  const restaurer = useRestoreFormTemplate();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const archiveBtnRef = useRef<HTMLButtonElement | null>(null);

  const template = detail.data?.template;

  if (detail.isLoading && !detail.data) {
    return <DetailSkeleton />;
  }

  if (detail.error) {
    const notFound = detail.error instanceof ApiException && detail.error.status === 404;
    return (
      <div className="mx-auto w-full max-w-3xl px-1 py-8">
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700"
        >
          <p className="font-medium">
            {notFound
              ? t("form_templates.detail.not_found")
              : detail.error instanceof ApiException
                ? getApiErrorMessage(detail.error, lang) || t("errors.generic")
                : t("errors.generic")}
          </p>
          <Link
            to="/formulaires"
            className="mt-3 inline-flex rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            {t("form_templates.detail.back_to_list")}
          </Link>
        </div>
      </div>
    );
  }

  if (!template) return null;

  const isArchived = template.status === "archived";

  // CTA « Remplir » (1d-ii) : actif seulement si le template a ≥1 version publiée ;
  // navigue vers la DERNIÈRE version publiée (la page de remplissage offre un
  // sélecteur pour choisir une ancienne version publiée). Jamais de lien mort.
  const latestPublished = (versions.data?.items ?? [])
    .filter((v) => v.published_at !== null)
    .reduce<FormTemplateVersionRow | null>(
      (acc, v) => (!acc || v.version_number > acc.version_number ? v : acc),
      null,
    );

  return (
    <div className="mx-auto w-full max-w-3xl px-1 py-6">
      <nav className="mb-4 text-sm">
        <Link
          to="/formulaires"
          className="hover:text-brand-700 focus-visible:ring-brand-500 rounded-sm text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          ← {t("form_templates.detail.back_to_list")}
        </Link>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {template.name}
            </h1>
            <FormTemplateStatusBadge status={template.status} />
          </div>
          <p className="mt-1 text-sm text-slate-500">
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
              {template.code}
            </code>
            <span className="ml-2 uppercase">{template.default_language}</span>
          </p>
        </div>
        {!isArchived && (
          <div className="flex shrink-0 items-center gap-2">
            {latestPublished && (
              <Link
                to={formFillPath(template.id, latestPublished.id)}
                className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {t("form_templates.actions.fill")}
              </Link>
            )}
            <Link
              to={`/reponses?template_id=${template.id}`}
              className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("form_templates.actions.view_responses")}
            </Link>
            <button
              ref={archiveBtnRef}
              type="button"
              onClick={() => setArchiveOpen(true)}
              className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("form_templates.actions.archive")}
            </button>
          </div>
        )}
      </header>

      {isArchived && (
        <div
          role="status"
          className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
        >
          <p className="font-medium">{t("form_templates.detail.archived_banner_title")}</p>
          <p className="mt-1 text-slate-600">{t("form_templates.detail.archived_banner_body")}</p>
          {/* 9.BJ.1 — le geste inverse, là où on constate le problème. Le
              chercher ailleurs, c'est ce qui a fait abandonner le courtier
              pilote après avoir archivé son gabarit par erreur. */}
          <button
            type="button"
            onClick={() => restaurer.mutate(template.id)}
            disabled={restaurer.isPending}
            className="focus-visible:ring-brand-500 mt-3 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {restaurer.isPending ? t("app.loading") : t("form_templates.actions.restore")}
          </button>
        </div>
      )}

      {/* Métadonnées */}
      <section className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("form_templates.detail.metadata_title")}
        </h2>
        {isArchived ? (
          <MetadataReadOnly template={template} />
        ) : (
          <MetadataForm
            key={template.updated_at}
            template={template}
            onArchivedRace={() => detail.refetch()}
          />
        )}
      </section>

      {/* Versions */}
      <section className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("form_templates.detail.versions_title")}
        </h2>
        <VersionsPanel
          templateId={template.id}
          isArchived={isArchived}
          versionsData={versions.data?.items}
          isLoading={versions.isLoading && !versions.data}
          error={versions.error}
          lang={lang}
        />
      </section>

      {archiveOpen && (
        <ArchiveFormTemplateDialog
          templateId={template.id}
          templateName={template.name}
          onClose={() => {
            setArchiveOpen(false);
            queueMicrotask(() => archiveBtnRef.current?.focus());
          }}
          onArchived={() => {
            setArchiveOpen(false);
            // La query détail est invalidée par le hook → refetch → lecture seule.
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Métadonnées — édition (actif) vs lecture seule (archivé)
// ---------------------------------------------------------------------------

function MetadataForm({
  template,
  onArchivedRace,
}: {
  template: FormTemplateRow;
  onArchivedRace: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const update = useUpdateFormTemplate(template.id);
  const { user } = useAuth();
  const canClassify = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [catalogUsage, setCatalogUsage] = useState(template.catalog_usage ?? "unclassified");

  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? "");
  const [tags, setTags] = useState<string[]>([...template.tags]);
  const [nameError, setNameError] = useState<string | null>(null);

  const baseline = useMemo(
    () => ({
      name: template.name,
      description: template.description ?? "",
      tags: template.tags,
    }),
    [template.name, template.description, template.tags],
  );

  const dirty =
    catalogUsage !== (template.catalog_usage ?? "unclassified") ||
    name !== baseline.name ||
    description !== baseline.description ||
    !sameTags(tags, baseline.tags);

  const { allowNavigation } = useUnsavedChangesGuard(
    dirty,
    t("form_templates.detail.unsaved_message"),
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || update.isPending) return;
    if (name.trim() === "") {
      setNameError(t("form_templates.create.errors.name_required"));
      return;
    }
    // N'envoie QUE les champs modifiables réellement modifiés (jamais code/status/…).
    const patch: Record<string, unknown> = {};
    if (canClassify && catalogUsage !== (template.catalog_usage ?? "unclassified"))
      patch.catalog_usage = catalogUsage;
    if (name !== baseline.name) patch.name = name.trim();
    if (description !== baseline.description)
      patch.description = description.trim() === "" ? null : description.trim();
    if (!sameTags(tags, baseline.tags)) patch.tags = tags;

    update.mutate(patch, {
      onSuccess: () => {
        allowNavigation();
        toast.success(t("form_templates.detail.metadata_saved"));
        // Le hook invalide detail → refetch → nouvelle baseline (key=updated_at remonte le form).
      },
      onError: (err) => {
        if (
          err instanceof ApiException &&
          err.status === 409 &&
          err.apiError.code === "FORM_TEMPLATE_ARCHIVED"
        ) {
          toast.info(t("form_templates.detail.archived_during_edit"));
          onArchivedRace();
        }
      },
    });
  };

  const genericError =
    update.isError &&
    !(
      update.error instanceof ApiException &&
      update.error.status === 409 &&
      update.error.apiError.code === "FORM_TEMPLATE_ARCHIVED"
    )
      ? update.error instanceof ApiException
        ? getApiErrorMessage(update.error, lang) || t("errors.generic")
        : t("errors.generic")
      : null;

  return (
    <form
      onSubmit={onSubmit}
      className="mt-3 space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      {genericError && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {genericError}
        </div>
      )}

      <div>
        <label htmlFor="ft-catalog" className="block text-sm font-medium text-slate-700">
          {t("form_templates.catalog.label")}
        </label>
        <select
          id="ft-catalog"
          value={catalogUsage}
          disabled={!canClassify || update.isPending}
          onChange={(e) => setCatalogUsage(Schemas.FormCatalogUsageSchema.parse(e.target.value))}
          aria-describedby="ft-catalog-help"
          className="mt-1 min-h-11 rounded-lg border border-slate-300 px-3 text-base"
        >
          {(["unclassified", "business", "test"] as const).map((value) => (
            <option key={value} value={value}>
              {t(`form_templates.catalog.${value}`)}
            </option>
          ))}
        </select>
        <p id="ft-catalog-help" className="mt-2 text-sm text-slate-600">
          {t("form_templates.catalog.help")}
        </p>
      </div>
      <div>
        <label htmlFor="ft-name" className="block text-sm font-medium text-slate-700">
          {t("form_templates.fields.name")}
        </label>
        <input
          id="ft-name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          maxLength={300}
          aria-invalid={nameError ? true : undefined}
          aria-describedby={nameError ? "ft-name-error" : undefined}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2"
        />
        {nameError && (
          <p id="ft-name-error" role="alert" className="mt-1 text-xs text-red-600">
            {nameError}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="ft-desc" className="block text-sm font-medium text-slate-700">
          {t("form_templates.fields.description")}
          <span className="ml-1 font-normal text-slate-400">
            {t("form_templates.fields.optional")}
          </span>
        </label>
        <textarea
          id="ft-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={2000}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      <div>
        <label htmlFor="ft-tags" className="block text-sm font-medium text-slate-700">
          {t("form_templates.fields.tags")}
          <span className="ml-1 font-normal text-slate-400">
            {t("form_templates.fields.optional")}
          </span>
        </label>
        <div className="mt-1">
          <TagsInput
            id="ft-tags"
            value={tags}
            onChange={setTags}
            placeholder={t("form_templates.fields.tags_placeholder")}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
        <button
          type="submit"
          disabled={!dirty || update.isPending}
          aria-busy={update.isPending}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {update.isPending && (
            <svg
              className="h-4 w-4 animate-spin motion-reduce:animate-none"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
              <path
                d="M14 8a6 6 0 0 1-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
          {update.isPending ? t("form_templates.detail.saving") : t("form_templates.detail.save")}
        </button>
      </div>
    </form>
  );
}

function MetadataReadOnly({ template }: { template: FormTemplateRow }) {
  const { t } = useTranslation();
  return (
    <dl className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-white p-5 text-sm shadow-sm">
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {t("form_templates.fields.name")}
        </dt>
        <dd className="mt-0.5 text-slate-900">{template.name}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {t("form_templates.fields.description")}
        </dt>
        <dd className="mt-0.5 text-slate-700">
          {template.description ?? <span className="text-slate-400">—</span>}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {t("form_templates.fields.tags")}
        </dt>
        <dd className="mt-0.5">
          {template.tags.length ? (
            <span className="flex flex-wrap gap-1">
              {template.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                >
                  {tag}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </dd>
      </div>
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------

function VersionsPanel({
  templateId,
  isArchived,
  versionsData,
  isLoading,
  error,
  lang,
}: {
  templateId: string;
  isArchived: boolean;
  versionsData: FormTemplateVersionRow[] | undefined;
  isLoading: boolean;
  error: unknown;
  lang: "fr" | "en";
}) {
  const { t } = useTranslation();
  // Hooks AVANT tout early-return (rules-of-hooks).
  const navigate = useNavigate();
  const createVersion = useCreateFormVersion(templateId);
  const [publishTarget, setPublishTarget] = useState<{ id: string; number: number } | null>(null);

  if (isLoading) {
    return (
      <div className="mt-3 space-y-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="h-12 rounded-lg bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none"
          />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div
        role="alert"
        className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        {t("errors.generic")}
      </div>
    );
  }

  const items = versionsData ?? [];
  const activeDraft = items.find((v) => v.published_at === null);
  const hasPublished = items.some((v) => v.published_at !== null);

  const onPublishClick = (v: FormTemplateVersionRow) => {
    // Pré-check client des rôles (1e-B) — microcopie utile AVANT le contrat strict.
    // Le serveur reste la défense authoritative (revalide au publish).
    const sections =
      (v.schema as { sections?: Parameters<typeof Schemas.collectFieldRoleIssues>[0] }).sections ??
      [];
    const roleIssues = Schemas.collectFieldRoleIssues(sections);
    if (roleIssues.length > 0) {
      const first = roleIssues[0]!;
      const roleLabel = t(`form_templates.editor.roles.${first.role}`);
      toast.error(
        first.kind === "type_mismatch"
          ? t("form_templates.publish.role_type_mismatch", { role: roleLabel })
          : t("form_templates.publish.role_duplicate", { role: roleLabel }),
      );
      return;
    }
    // 9.BJ.7 — un bloc qui cite un champ inexistant. Le serveur refuse déjà (cf.
    // `FormVersionSchemaContract`) ; ce pré-check nomme le bloc et la clé, là où
    // le contrat strict ne dirait que « schéma invalide ».
    const refIssues = Schemas.collectBlockRefIssues(sections);
    if (refIssues.length > 0) {
      const first = refIssues[0]!;
      toast.error(
        t("form_templates.publish.block_ref_unknown", {
          block: first.block_key,
          refs: first.unknown_refs.map((k: string) => `{{${k}}}`).join(", "),
        }),
      );
      return;
    }
    // Pré-check STRICT client (§7) : schéma non vide/valide avant d'ouvrir le dialogue.
    if (!Schemas.FormVersionSchemaContract.safeParse(v.schema).success) {
      toast.error(t("form_templates.publish.empty_blocked"));
      return;
    }
    setPublishTarget({ id: v.id, number: v.version_number });
  };

  const onNewVersion = () => {
    if (createVersion.isPending) return;
    createVersion.mutate(undefined, {
      onSuccess: (v) => navigate(formVersionEditorPath(templateId, v.id)),
      onError: (err) => {
        // 409 brouillon actif existant → redirection vers ce brouillon (pas d'erreur bloquante, §9).
        if (
          err instanceof ApiException &&
          err.status === 409 &&
          err.apiError.code === "FORM_VERSION_ACTIVE_DRAFT_EXISTS"
        ) {
          const details = err.apiError.details as { draft_version_id?: unknown } | undefined;
          const draftId = details?.draft_version_id;
          if (typeof draftId === "string" && UUID_RE.test(draftId)) {
            toast.info(t("form_templates.versions.draft_exists_redirect"));
            navigate(formVersionEditorPath(templateId, draftId));
          }
        }
      },
    });
  };

  return (
    <div className="mt-3 space-y-3">
      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white shadow-sm">
        {items.map((v) => (
          <li
            key={v.id}
            className="flex flex-col gap-2 px-4 py-3 text-sm sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="font-medium tabular-nums text-slate-900">v{v.version_number}</span>
                <FormVersionStateBadge publishedAt={v.published_at} />
                <span className="text-xs tabular-nums text-slate-500">
                  {v.published_at
                    ? t("form_templates.versions.published_on", {
                        date: formatDate(v.published_at, lang),
                      })
                    : t("form_templates.versions.created_on", {
                        date: formatDate(v.created_at, lang),
                      })}
                </span>
              </div>
              {v.changelog && (
                <p className="mt-1 break-words text-xs text-slate-600">{v.changelog}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {v.published_at === null ? (
                !isArchived && (
                  <>
                    <Link
                      to={formVersionEditorPath(templateId, v.id)}
                      className="focus-visible:ring-brand-500 touch-manipulation rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    >
                      {t("form_templates.detail.edit_draft")}
                    </Link>
                    <button
                      type="button"
                      onClick={() => onPublishClick(v)}
                      className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-1.5 text-xs font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    >
                      {t("form_templates.publish.cta")}
                    </button>
                  </>
                )
              ) : (
                <Link
                  to={formVersionEditorPath(templateId, v.id)}
                  className="focus-visible:ring-brand-500 touch-manipulation rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  {t("form_templates.versions.view")}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Nouvelle version : template actif, aucun brouillon actif, au moins une publiée (§9). */}
      {!isArchived && !activeDraft && hasPublished && (
        <button
          type="button"
          onClick={onNewVersion}
          disabled={createVersion.isPending}
          className="border-brand-300 bg-brand-50/50 text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          <span aria-hidden="true">+</span> {t("form_templates.versions.new_version")}
        </button>
      )}

      {publishTarget && (
        <PublishFormVersionDialog
          templateId={templateId}
          versionId={publishTarget.id}
          versionNumber={publishTarget.number}
          onClose={() => setPublishTarget(null)}
          onPublished={() => setPublishTarget(null)}
        />
      )}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto w-full max-w-3xl px-1 py-6">
      <div className="h-4 w-24 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      <div className="mt-4 h-8 w-64 rounded bg-slate-200 motion-safe:animate-pulse motion-reduce:animate-none" />
      <div className="mt-6 h-48 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
    </div>
  );
}
